import { useState, useEffect } from 'react';
import CreateCustomerModal from './CreateCustomerModal';
import PhoneLookupModal from './PhoneLookupModal';
import CardLookupModal from './CardLookupModal';
import TransactionDetailsModal from './TransactionDetailsModal';
import SalespersonSelector, { Salesperson } from './SalespersonSelector';

interface CustomerPanelProps {
  clearTrigger?: number;
}

export default function CustomerPanel({ clearTrigger }: CustomerPanelProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPhoneLookup, setShowPhoneLookup] = useState(false);
  const [showCardLookup, setShowCardLookup] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [selectedSalesperson, setSelectedSalesperson] = useState<Salesperson | null>(null);

  // Clear customer and salesperson when clearTrigger changes
  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      setSelectedCustomer(null);
      setSelectedSalesperson(null);
    }
  }, [clearTrigger]);

  // Mock customer database
  const mockCustomers = [
    { id: 'C001', name: 'Sarah Johnson', phone: '555-0123', email: 'sarah.j@email.com', loyaltyPoints: 1250, memberSince: '2023-01-15' },
    { id: 'C002', name: 'Michael Chen', phone: '555-0124', email: 'mchen@email.com', loyaltyPoints: 890, memberSince: '2023-03-22' },
    { id: 'C003', name: 'Emily Rodriguez', phone: '555-0125', email: 'emily.r@email.com', loyaltyPoints: 2340, memberSince: '2022-11-08' },
    { id: 'C004', name: 'David Kim', phone: '555-0126', email: 'dkim@email.com', loyaltyPoints: 450, memberSince: '2024-01-05' },
    { id: 'C005', name: 'Jessica Martinez', phone: '555-0127', email: 'jmartinez@email.com', loyaltyPoints: 1680, memberSince: '2023-06-12' },
  ];

  // Mock recent transactions with full details
  const recentTransactions = [
    { 
      id: 'TXN-2847', 
      time: '14:23', 
      date: '2024-01-15',
      amount: '$127.45', 
      items: 12,
      cashier: 'Sarah Johnson',
      customer: 'Michael Chen',
      paymentMethod: 'Credit Card',
      subtotal: 115.00,
      tax: 12.45,
      discount: 0,
      total: 127.45,
      itemsList: [
        { id: 'I1', name: 'Organic Bananas', sku: 'FRT-001', quantity: 3, unitPrice: 2.99, discount: 0, total: 8.97 },
        { id: 'I2', name: 'Whole Milk 1L', sku: 'DRY-045', quantity: 2, unitPrice: 3.49, discount: 0, total: 6.98 },
        { id: 'I3', name: 'Sourdough Bread', sku: 'BKY-012', quantity: 1, unitPrice: 4.99, discount: 0, total: 4.99 },
        { id: 'I4', name: 'Free Range Eggs (12)', sku: 'DRY-089', quantity: 2, unitPrice: 5.99, discount: 0, total: 11.98 },
        { id: 'I5', name: 'Organic Spinach', sku: 'VEG-034', quantity: 1, unitPrice: 3.99, discount: 0, total: 3.99 },
        { id: 'I6', name: 'Greek Yogurt 500g', sku: 'DRY-067', quantity: 3, unitPrice: 4.49, discount: 0, total: 13.47 },
        { id: 'I7', name: 'Cherry Tomatoes', sku: 'VEG-078', quantity: 2, unitPrice: 3.99, discount: 0, total: 7.98 },
        { id: 'I8', name: 'Avocados (3 pack)', sku: 'FRT-023', quantity: 1, unitPrice: 5.99, discount: 0, total: 5.99 },
        { id: 'I9', name: 'Cheddar Cheese 200g', sku: 'DRY-156', quantity: 1, unitPrice: 6.99, discount: 0, total: 6.99 },
        { id: 'I10', name: 'Orange Juice 1L', sku: 'BEV-034', quantity: 2, unitPrice: 4.99, discount: 0, total: 9.98 },
        { id: 'I11', name: 'Pasta Penne 500g', sku: 'DRY-234', quantity: 3, unitPrice: 2.99, discount: 0, total: 8.97 },
        { id: 'I12', name: 'Olive Oil 500ml', sku: 'OIL-012', quantity: 1, unitPrice: 8.99, discount: 0, total: 8.99 },
      ]
    },
    { 
      id: 'TXN-2846', 
      time: '14:18', 
      date: '2024-01-15',
      amount: '$89.20', 
      items: 8,
      cashier: 'Sarah Johnson',
      paymentMethod: 'Cash',
      subtotal: 80.50,
      tax: 8.70,
      discount: 0,
      total: 89.20,
      itemsList: [
        { id: 'I1', name: 'Coffee Beans 250g', sku: 'BEV-089', quantity: 2, unitPrice: 12.99, discount: 0, total: 25.98 },
        { id: 'I2', name: 'Almond Milk 1L', sku: 'DRY-078', quantity: 1, unitPrice: 4.99, discount: 0, total: 4.99 },
        { id: 'I3', name: 'Granola 500g', sku: 'BRK-045', quantity: 1, unitPrice: 6.99, discount: 0, total: 6.99 },
        { id: 'I4', name: 'Honey 350g', sku: 'SWT-023', quantity: 1, unitPrice: 8.99, discount: 0, total: 8.99 },
        { id: 'I5', name: 'Blueberries 250g', sku: 'FRT-067', quantity: 2, unitPrice: 5.99, discount: 0, total: 11.98 },
        { id: 'I6', name: 'Croissants (4 pack)', sku: 'BKY-034', quantity: 1, unitPrice: 5.99, discount: 0, total: 5.99 },
        { id: 'I7', name: 'Butter 250g', sku: 'DRY-123', quantity: 1, unitPrice: 4.99, discount: 0, total: 4.99 },
        { id: 'I8', name: 'Strawberry Jam 340g', sku: 'SWT-056', quantity: 1, unitPrice: 4.99, discount: 0, total: 4.99 },
      ]
    },
    { 
      id: 'TXN-2845', 
      time: '14:12', 
      date: '2024-01-15',
      amount: '$234.67', 
      items: 18,
      cashier: 'Sarah Johnson',
      customer: 'Emily Rodriguez',
      paymentMethod: 'Debit Card',
      subtotal: 212.00,
      tax: 22.67,
      discount: 0,
      total: 234.67,
      itemsList: [
        { id: 'I1', name: 'Salmon Fillet 500g', sku: 'FSH-012', quantity: 2, unitPrice: 15.99, discount: 0, total: 31.98 },
        { id: 'I2', name: 'Chicken Breast 1kg', sku: 'MET-045', quantity: 1, unitPrice: 12.99, discount: 0, total: 12.99 },
        { id: 'I3', name: 'Broccoli', sku: 'VEG-089', quantity: 3, unitPrice: 2.99, discount: 0, total: 8.97 },
        { id: 'I4', name: 'Sweet Potatoes 1kg', sku: 'VEG-123', quantity: 1, unitPrice: 3.99, discount: 0, total: 3.99 },
        { id: 'I5', name: 'Red Wine 750ml', sku: 'ALC-034', quantity: 2, unitPrice: 18.99, discount: 0, total: 37.98 },
        { id: 'I6', name: 'Parmesan Cheese 200g', sku: 'DRY-234', quantity: 1, unitPrice: 9.99, discount: 0, total: 9.99 },
        { id: 'I7', name: 'Pasta Sauce 500g', sku: 'SRC-067', quantity: 3, unitPrice: 4.99, discount: 0, total: 14.97 },
        { id: 'I8', name: 'Garlic Bread', sku: 'BKY-078', quantity: 2, unitPrice: 3.99, discount: 0, total: 7.98 },
        { id: 'I9', name: 'Caesar Salad Kit', sku: 'SLD-045', quantity: 2, unitPrice: 5.99, discount: 0, total: 11.98 },
        { id: 'I10', name: 'Ice Cream 1L', sku: 'FRZ-023', quantity: 2, unitPrice: 6.99, discount: 0, total: 13.98 },
        { id: 'I11', name: 'Potato Chips 200g', sku: 'SNK-089', quantity: 4, unitPrice: 3.99, discount: 0, total: 15.96 },
        { id: 'I12', name: 'Sparkling Water 1L', sku: 'BEV-156', quantity: 6, unitPrice: 1.99, discount: 0, total: 11.94 },
        { id: 'I13', name: 'Dark Chocolate 100g', sku: 'SWT-234', quantity: 3, unitPrice: 4.99, discount: 0, total: 14.97 },
        { id: 'I14', name: 'Almonds 250g', sku: 'NUT-045', quantity: 2, unitPrice: 7.99, discount: 0, total: 15.98 },
        { id: 'I15', name: 'Green Tea Bags', sku: 'BEV-234', quantity: 1, unitPrice: 5.99, discount: 0, total: 5.99 },
        { id: 'I16', name: 'Hummus 200g', sku: 'DIP-067', quantity: 2, unitPrice: 4.99, discount: 0, total: 9.98 },
        { id: 'I17', name: 'Pita Bread', sku: 'BKY-123', quantity: 1, unitPrice: 3.99, discount: 0, total: 3.99 },
        { id: 'I18', name: 'Olives 350g', sku: 'DLI-089', quantity: 1, unitPrice: 6.99, discount: 0, total: 6.99 },
      ]
    },
    { 
      id: 'TXN-2844', 
      time: '14:05', 
      date: '2024-01-15',
      amount: '$56.30', 
      items: 5,
      cashier: 'Sarah Johnson',
      paymentMethod: 'Credit Card',
      subtotal: 51.00,
      tax: 5.30,
      discount: 0,
      total: 56.30,
      itemsList: [
        { id: 'I1', name: 'Laundry Detergent 2L', sku: 'CLN-045', quantity: 1, unitPrice: 12.99, discount: 0, total: 12.99 },
        { id: 'I2', name: 'Dish Soap 500ml', sku: 'CLN-089', quantity: 2, unitPrice: 4.99, discount: 0, total: 9.98 },
        { id: 'I3', name: 'Paper Towels (6 pack)', sku: 'PPR-023', quantity: 1, unitPrice: 8.99, discount: 0, total: 8.99 },
        { id: 'I4', name: 'Toilet Paper (12 pack)', sku: 'PPR-067', quantity: 1, unitPrice: 12.99, discount: 0, total: 12.99 },
        { id: 'I5', name: 'Sponges (5 pack)', sku: 'CLN-234', quantity: 1, unitPrice: 5.99, discount: 0, total: 5.99 },
      ]
    },
  ];

  const handleCustomerCreated = (customer: any) => {
    setSelectedCustomer(customer);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
  };

  const handleTransactionClick = (txn: any) => {
    setSelectedTransaction(txn);
    setShowTransactionDetails(true);
  };

  return (
    <>
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-900 whitespace-nowrap">Customer</h2>
            {selectedCustomer && (
              <button
                onClick={handleClearCustomer}
                className="text-xs text-red-600 hover:text-red-700 font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-circle-line mr-1"></i>
                Clear
              </button>
            )}
          </div>
          
          {!selectedCustomer ? (
            <div className="w-full space-y-2">
              <button 
                onClick={() => setShowPhoneLookup(true)}
                className="w-full px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line text-base"></i>
                By Phone
              </button>
              
              <button 
                onClick={() => setShowCardLookup(true)}
                className="w-full px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-bank-card-line text-base"></i>
                By Card
              </button>
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-user-add-line text-base"></i>
                New Customer
              </button>
            </div>
          ) : (
            <div className="bg-teal-50 border-2 border-teal-500 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-full font-bold text-sm">
                    {selectedCustomer.name.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{selectedCustomer.name}</p>
                    <p className="text-xs text-gray-600">{selectedCustomer.id}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-phone-line"></i>
                  <span>{selectedCustomer.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-mail-line"></i>
                  <span>{selectedCustomer.email}</span>
                </div>
                <div className="flex items-center gap-2 text-teal-700 font-semibold mt-2">
                  <i className="ri-vip-crown-line"></i>
                  <span>{selectedCustomer.loyaltyPoints} Points</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Salesperson Assignment Section */}
        <div className="p-4 border-b border-gray-200">
          <SalespersonSelector
            selectedSalesperson={selectedSalesperson}
            onSelect={setSelectedSalesperson}
            userRole="manager"
            required={false}
          />
          {selectedSalesperson && (
            <div className="mt-2 p-2 bg-teal-50 rounded-lg">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Today&apos;s Sales</span>
                <span className="font-semibold text-gray-900">${selectedSalesperson.currentDaySales.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-gray-600">Commission Earned</span>
                <span className="font-semibold text-teal-600">${selectedSalesperson.commissionEarned.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-b border-gray-200">
          <h3 className="text-xs font-bold text-gray-700 mb-3 whitespace-nowrap">Transaction Settings</h3>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600 whitespace-nowrap">Price List</span>
              <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">Standard Retail</span>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600 whitespace-nowrap">Tax Profile</span>
              <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">Standard VAT</span>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
              <span className="text-xs text-gray-600 whitespace-nowrap">Promo Engine</span>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                <span className="text-xs font-semibold text-green-700 whitespace-nowrap">Active</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <h3 className="text-xs font-bold text-gray-700 mb-3 whitespace-nowrap">Recent Transactions</h3>
          
          <div className="space-y-2">
            {recentTransactions.map((txn) => (
              <div 
                key={txn.id} 
                onClick={() => handleTransactionClick(txn)}
                className="p-2 bg-gray-50 hover:bg-gray-100 rounded cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">{txn.id}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{txn.time}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 whitespace-nowrap">{txn.items} items</span>
                  <span className="text-xs font-bold text-teal-600 whitespace-nowrap">{txn.amount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateCustomerModal
          onClose={() => setShowCreateModal(false)}
          onCustomerCreated={handleCustomerCreated}
        />
      )}

      {showPhoneLookup && (
        <PhoneLookupModal 
          onClose={() => setShowPhoneLookup(false)}
          onSelectCustomer={setSelectedCustomer}
        />
      )}

      {showCardLookup && (
        <CardLookupModal 
          onClose={() => setShowCardLookup(false)}
          onSelectCustomer={setSelectedCustomer}
        />
      )}

      {showTransactionDetails && selectedTransaction && (
        <TransactionDetailsModal
          onClose={() => setShowTransactionDetails(false)}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
}
